import { ReactElement, useCallback, useMemo } from 'react';

import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';

import { Button, Flex, HStack, Tag, Text, VStack } from 'components/uikit';
import { AlfaIcon } from 'components/uikit/icons/Alfa';
import { GazpromIcon } from 'components/uikit/icons/Gazprom';
import { MTSIcon } from 'components/uikit/icons/MTS';
import { SberIcon } from 'components/uikit/icons/Sber';
import { TBankIcon } from 'components/uikit/icons/TBank';
import { VTBIcon } from 'components/uikit/icons/VTB';
import {
  banksByMethod,
  DepositBank,
  DepositCredentials,
  DepositMethod,
} from 'data/payments/models';
import { AnalyticsEvents, trackEvent } from 'utils/analytics';
import { getDeeplinks } from 'utils/bankDeepLinks';
import { isEmpty } from 'utils/helpers';
import { useIsMobile } from 'utils/hooks/screenBreakpoints';
import {
  openDeeplink,
  openDeeplinksWithReload,
  processDeeplinksWithReload,
} from 'utils/openDeeplink';

interface BankButtonStyle {
  svg: ReactElement;
  backgroundImage?: string;
  backgroundColor?: string;
  fontColor: string;
}

const buttonStyles: Record<DepositBank, BankButtonStyle> = {
  [DepositBank.Values.Сбербанк]: {
    svg: <SberIcon w='72px' h='100%' />,
    backgroundImage:
      'linear-gradient(to right, rgb(100, 238, 97), rgb(50, 184, 211))',
    fontColor: '#fff',
  },
  [DepositBank.Values.ВТБ]: {
    svg: <VTBIcon w='60px' h='100%' />,
    backgroundColor: '#0a2973',
    fontColor: '#fff',
  },
  [DepositBank.Values['Т-Банк']]: {
    svg: <TBankIcon w='72px' h='100%' />,
    backgroundColor: '#FFDD2D',
    fontColor: '#333',
  },
  [DepositBank.Values['МТС Банк']]: {
    svg: <MTSIcon w='72px' h='100%' />,
    backgroundColor: '#FF0032',
    fontColor: '#fff',
  },
  [DepositBank.Values.Газпромбанк]: {
    svg: <GazpromIcon w='72px' h='100%' />,
    backgroundColor: '#0079C8',
    fontColor: '#fff',
  },
  [DepositBank.Values['Альфа-Банк']]: {
    svg: <AlfaIcon w='72px' h='100%' />,
    backgroundColor: '#EE2A23',
    fontColor: '#fff',
  },
};

export const getBanksByMethod = (
  method: DepositMethod | null,
  fromBank: string | null,
) => {
  if (!method) return [];

  const banks = banksByMethod[method] ?? [];

  if (!fromBank) return banks;

  return banks.filter((bank) => bank === fromBank);
};

export interface BankDeeplinkButtonProps {
  id: string;
  method: DepositMethod;
  amount: string;
  bank: DepositBank;
  credentials: DepositCredentials;
}

const BankDeeplinkButton = ({
  id,
  method,
  bank,
  amount,
  credentials,
}: BankDeeplinkButtonProps) => {
  const { t } = useTranslation('payments');
  const { svg, backgroundImage, backgroundColor, fontColor } =
    buttonStyles[bank];
  const links = useMemo(() => {
    return getDeeplinks(
      method,
      credentials.type,
      bank,
      amount,
      credentials.value ?? '',
      credentials.bank,
    );
  }, [
    credentials.type,
    method,
    bank,
    amount,
    credentials.value,
    credentials.bank,
  ]);

  const openLink = useCallback(async () => {
    if (isEmpty(links)) return;

    trackEvent(AnalyticsEvents.PAYMENT_LINK_CLICKED, {
      order_id: id,
      payment_method: method,
      amount_base: amount,
    });

    if (links.length > 1) {
      const started = openDeeplinksWithReload(links, id);
      if (started) {
        await processDeeplinksWithReload(id);
      }
    } else if (links[0]) {
      await openDeeplink(links[0]);
    }
  }, [links, id, amount, method]);

  if (isEmpty(links)) return null;

  return (
    <Button
      w='100%'
      p='0'
      backgroundImage={backgroundImage}
      backgroundColor={backgroundColor}
      _hover={{
        backgroundColor,
      }}
      onClick={openLink}
    >
      <HStack h='100%' alignItems='center' spacing='6px'>
        <Text color={fontColor} fontSize='lg' fontWeight='semibold'>
          {t('pay_in_app')}
        </Text>
        {svg}
      </HStack>
    </Button>
  );
};

interface BankDeeplinkButtonsProps {
  method: DepositMethod | null;
  amount: string;
  fromBank: string | null;
  credentials: DepositCredentials;
  id: string;
}

export const BankDeeplinkButtons = ({
  method,
  amount,
  fromBank,
  credentials,
  id,
}: BankDeeplinkButtonsProps) => {
  const availableBanks = getBanksByMethod(method, fromBank);
  const isMobile = useIsMobile();
  const { t } = useTranslation('payment');

  if (method === null) return null;

  if (['VND_TG_QR', 'PHP_TG_QR'].includes(method)) {
    const hasDeeplinks = availableBanks.some(
      (bank) =>
        getDeeplinks(
          method,
          credentials.type,
          bank,
          amount,
          credentials.value ?? '',
          credentials.bank,
        ).length > 0,
    );

    if (hasDeeplinks) {
      return (
        <VStack w='100%'>
          {availableBanks.map((bank) => (
            <BankDeeplinkButton
              id={id}
              method={method}
              key={bank}
              bank={bank}
              amount={amount}
              credentials={credentials}
            />
          ))}
        </VStack>
      );
    }
    return (
      <Tag
        borderRadius='8px'
        colorScheme='indigo'
        w='100%'
        p='16px'
        flexDir='row'
        fontSize='xl'
        fontWeight='medium'
        stroke='unset'
      >
        {t('pay_qr')}
        <Flex borderRadius='8px' p='8px' background='grey.background'>
          <QRCodeSVG
            bgColor='#fff'
            fgColor='#000'
            value={credentials.value ?? ''}
            size={120}
          />
        </Flex>
      </Tag>
    );
  }
  if (availableBanks.length === 1 && !isMobile)
    return (
      <Tag
        borderRadius='8px'
        colorScheme='indigo'
        w='100%'
        p='16px'
        flexDir='row'
        fontSize='xl'
        fontWeight='medium'
        stroke='unset'
      >
        {t('pay_qr')}
        <Flex borderRadius='8px' p='8px' background='grey.background'>
          <QRCodeSVG
            bgColor='#fff'
            fgColor='#000'
            value={`${window.location.origin}/${id}/deeplink`}
            size={120}
          />
        </Flex>
      </Tag>
    );

  if (availableBanks.length !== 1 && !isMobile) return null;

  return (
    <VStack w='100%'>
      {availableBanks.map((bank) => (
        <BankDeeplinkButton
          id={id}
          method={method}
          key={bank}
          bank={bank}
          amount={amount}
          credentials={credentials}
        />
      ))}
    </VStack>
  );
};
